import React, { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import JjfShell from "../HomePage2/JjfShell";
import AboutHero from "../../components/aboutHero/AboutHero";
import "../AboutUsPage/aboutV2.css";
import AboutUs from "../../images/volunteer-opportunities/about-us-v2.jpg";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";

const VOLUNTEER_URL = "https://lifesaverarmy.com/volunteers";

/* ---- volunteer opportunity categories ---- */
const OPPORTUNITIES = [
  {
    title: "Skill-Based Volunteering",
    intro: "Use your expertise to drive meaningful impact.",
    points: [
      "Help coordinate Life Saver Army donor registration drives or blood donation camps.",
      "Support donor awareness campaigns targeting hospitals, universities, and public institutions in your area.",
      "Offer professional skills such as graphic design, digital communications, event logistics, data management or partnership outreach to support Life Saver Army's operations.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="m14 7 3 3M5 21l4-1 9.5-9.5a2.1 2.1 0 0 0-3-3L5 17l-1 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>),
  },
  {
    title: "One-Time Volunteering",
    intro: "Perfect for those with limited time but a desire to give back.",
    points: [
      "Participate in Life Saver Army blood donation camps or QR sticker deployment drives.",
      "Volunteer at awareness walks, public outreach events, or QR code sticker deployment drives across your city or district.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
  {
    title: "Ongoing Volunteering",
    intro: "Make a sustained difference as part of a long-term programme.",
    points: [
      "Volunteer as a Life Saver Army field coordinator or community liaison across our active states.",
      "Assist with donor database management, emergency blood request coordination, or follow-up communications with registered donors.",
      "Become a long-term Life Saver Army ambassador, driving registration drives in your community month after month.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M21 12a9 9 0 1 1-3-6.7M21 4v4h-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  },
  {
    title: "Virtual Volunteering",
    intro: "Contribute meaningfully from anywhere.",
    points: [
      "Spread awareness about Life Saver Army on social media; share donor registration links, real stories, and campaign content to grow the network.",
      "Assist with content creation, fundraising campaigns, or digital outreach.",
      "Offer administrative support such as data management or online research.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
  {
    title: "Community Volunteering",
    intro: "Work directly with local communities where impact is most visible.",
    points: [
      "Help organise blood donation camps with Life Saver Army.",
      "Support Life Saver Army's outreach in hospitals, Panchayat offices, and public spaces, helping place QR code stickers and distribute donor registration materials.",
      "Assist our NGO and hospital partners in building localised donor networks that respond quickly to patient needs.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  },
  {
    title: "Event Volunteering",
    intro: "Be part of the team that makes every Life Saver Army initiative happen.",
    points: [
      "Support event setup, registration desks, donor coordination, and on-ground activities during camps and awareness events.",
      "Help guide donors and participants throughout the event for a smooth experience.",
      "Assist the team with event coordination, crowd management, and post-event activities.",
    ],
    icon: (<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 10h18M8 3v4M16 3v4M9 15h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
];

/* ---- benefits ---- */
const BENEFITS = [
  {
    title: "Flexibility",
    des: "Volunteer on your schedule; once a week, once a month, or for a single event. We work around you.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /><path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  },
  {
    title: "Skill Development",
    des: "Gain hands-on experience in field coordination, campaign management, community engagement, and social impact.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M12 3 3 8l9 5 9-5-9-5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M7 10.5V15c0 .8 2.2 2.5 5 2.5s5-1.7 5-2.5v-4.5M21 8v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
  {
    title: "Networking",
    des: "Connect with passionate individuals, NGOs, and organisations who share a commitment to creating real and lasting change.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><circle cx="6" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.8" /><circle cx="18" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.8" /><circle cx="12" cy="18" r="2.5" stroke="currentColor" strokeWidth="1.8" /><path d="M7.5 7.5 11 16M16.5 7.5 13 16M8 6h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
  {
    title: "Fulfilment",
    des: "Make a tangible difference in people's lives; and be part of something greater than yourself.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-4.6-9.3-9C1.2 9 2.6 5.5 6 5.2c2-.2 3.4 1 4.4 2.4C11.4 9 12 10 12 10s.6-1 1.6-2.4C14.6 6.2 16 5 18 5.2c3.4.3 4.8 3.8 3.3 6.8C19 16.4 12 21 12 21Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>),
  },
];

/* ---- how to get started ---- */
const STEPS = [
  {
    n: "01",
    title: "Explore Opportunities",
    des: "Discover the different ways you can contribute to Life Saver Army. Whether it's donating blood, volunteering at donation camps, spreading awareness, or helping patients connect with donors, there's a role for everyone.",
  },
  {
    n: "02",
    title: "Register with Life Saver Army",
    des: "Complete the online registration form and let us know how you would like to support the mission of saving lives.",
  },
  {
    n: "03",
    title: "Get Connected",
    des: "Our team will connect you with nearby blood donation drives, emergency support requests, awareness campaigns, or volunteer opportunities based on your location and interests.",
  },
  {
    n: "04",
    title: "Start Saving Lives",
    des: "Become a part of the Life Saver Army community and begin making a real difference. With the guidance and support of the Jainam Jivika Foundation team, every contribution you make helps ensure that no one struggles alone in their search for blood.",
  },
];

/* ---- upcoming events ---- */
const EVENTS = [
  {
    title: "Blood Donation Camp",
    sub: "Life Saver Army",
    des: "Join Jainam Jivika Foundation's flagship initiative and help save lives through blood donation in your community.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>),
  },
  {
    title: "Platelet Donation Awareness Drive",
    des: "Join Life Saver Army in educating communities about platelet donation and registering new platelet donors for cancer and thalassaemia patients.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M3 12h4l2-5 4 10 2-5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>),
  },
  {
    title: "Hospital QR Code Deployment Drive",
    des: "Join our outreach team to deploy Life Saver Army QR code stickers at hospitals, clinics, and public spaces in your city or district.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8" /><path d="M14 14h3v3M21 14v.01M17 21h4M21 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
  {
    title: "University Blood Donor Registration Camp",
    des: "Volunteer at a Life Saver Army registration camp on a university or college campus and help students take their first step as voluntary blood donors.",
    icon: (<svg viewBox="0 0 24 24" fill="none"><path d="M12 3 2 8l10 5 10-5-10-5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M6 10.5V16c0 1.3 2.7 3 6 3s6-1.7 6-3v-5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>),
  },
];

const VolunteerApportunities = ({ path }) => {
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Volunteer Opportunities" });
  const [schemadata, setSchemadata] = useState({});

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];
    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      setMetadata({
        title: "JJFIndia | Volunteer Opportunities",
        description: "Volunteer with Jainam Jivika Foundation and Life Saver Army - your time, skills and passion can save lives.",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/join-us/volunteer-opportunities",
        openGraph: {
          title: "JJFIndia | Volunteer Opportunities",
          description: "Volunteer with Jainam Jivika Foundation and Life Saver Army.",
          url: "https://jjfindia.com/join-us/volunteer-opportunities",
        },
        twitter: { card: "summary_large_image", title: "JJFIndia | Volunteer Opportunities", description: "Volunteer with us" },
      });
    }
    if (tempSchemadata) setSchemadata(tempSchemadata);
  }, [path]);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <JjfShell>
        {/* ===================== PAGE TITLE ===================== */}
        <AboutHero
          eyebrow="Join Us"
          title="Volunteer Opportunities"
          crumbs={[
            { label: "Home", to: "/" },
            { label: "Join Us", to: "/join-us/volunteer-opportunities" },
            { label: "Volunteer Opportunities with Jainam Jivika Foundation" },
          ]}
        />

        {/* ===================== INTRO ===================== */}
        <section className="section about" style={{ paddingBottom: "32px" }}>
          <div className="wrap split">
            <div className="about-media reveal">
              {/* Wide group photo: plain rounded frame at the photo's own 4:3 shape so
                  every volunteer is shown whole (an arch or leaf would clip the outer heads). */}
              <span
                className="leaf-frame"
                aria-hidden="true"
                style={{ borderRadius: "calc(var(--r) + 14px)" }}
              ></span>
              <div className="ph" style={{ borderRadius: "var(--r)", aspectRatio: "4 / 3" }}>
                <img src={AboutUs} alt="Student volunteers holding Life Saver Army posters" loading="eager" />
              </div>
              <div className="stat-card reveal d2"><div className="n">140K+</div><div className="l">Volunteers joined</div></div>
            </div>
            <div className="about-copy reveal d1">
              <span className="eyebrow">Powered by people</span>
              <h2>Your Time, Skills and Passion Can Create Lasting Change</h2>
              <p className="lead">
                Life Saver Army, an initiative of Jainam Jivika Foundation, is powered by its people. Whether you want to help register blood donors, organise donation camps, manage outreach, or simply spread the word - there is a meaningful role for you here. Whatever your background, your contribution will help save lives.
              </p>
            </div>
          </div>
        </section>

        {/* ===================== EXPLORE OPPORTUNITIES ===================== */}
        <section className="section" id="opportunities" style={{ paddingTop: "24px" }}>
          <div className="wrap" style={{ textAlign: "center" }}>
            <span className="eyebrow reveal" style={{ justifyContent: "center" }}>Join hands, share skills, create change</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)" }}>Explore Our Volunteer Opportunities</h2>
          </div>
          <div className="wrap">
            <div className="opp-grid">
              {OPPORTUNITIES.map((o) => (
                <article className="opp-card reveal" key={o.title}>
                  <div className="oic">{o.icon}</div>
                  <h3>{o.title}</h3>
                  <p className="intro">{o.intro}</p>
                  <ul>
                    {o.points.map((p, i) => (<li key={i}>{p}</li>))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== BENEFITS ===================== */}
        <section className="section section--mist">
          <div className="wrap" style={{ textAlign: "center" }}>
            <span className="eyebrow reveal" style={{ justifyContent: "center" }}>Grow, connect and make a difference</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)" }}>Benefits of Volunteering with Jainam Jivika Foundation</h2>
          </div>
          <div className="wrap">
            <div className="ways">
              {BENEFITS.map((b, i) => (
                <article className={"way reveal" + (i % 4 ? " d" + (i % 4) : "")} key={b.title}>
                  <div className="wic">{b.icon}</div>
                  <h3>{b.title}</h3>
                  <p>{b.des}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== HOW TO GET STARTED ===================== */}
        <section className="section">
          <div className="wrap" style={{ textAlign: "center" }}>
            <span className="eyebrow reveal" style={{ justifyContent: "center" }}>Step in, stand out, make an impact</span>
            <h2 className="reveal d1" style={{ fontSize: "clamp(2rem,4.2vw,3.2rem)" }}>How to Get Started?</h2>
          </div>
          <div className="wrap">
            <div className="init-grid">
              {STEPS.map((s, i) => (
                <article className={"init reveal" + (i % 4 ? " d" + (i % 4) : "")} key={s.n}>
                  <span className="glow"></span>
                  <div className="badge"><span className="step-n">{s.n}</span></div>
                  <h3>{s.title}</h3>
                  <p>{s.des}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== UPCOMING EVENTS ===================== */}
        <section className="section section--mist">
          <div className="wrap">
            <div className="sec-head sec-head--stack">
              <div className="reveal">
                <span className="eyebrow">Be the change, one event at a time</span>
                <h2 style={{ lineHeight: 1.34 }}>Upcoming Volunteer Events</h2>
                <p className="lead reveal d1">
                Pick a drive near you and step in; every event is a chance to save a life.
              </p>
              </div>
            </div>
            <div className="init-grid">
              {EVENTS.map((e, i) => (
                <article className={"init reveal" + (i % 4 ? " d" + (i % 4) : "")} key={e.title}>
                  <span className="glow"></span>
                  <div className="badge">{e.icon}</div>
                  <h3>{e.title}</h3>
                  {e.sub && <span className="init-sub">{e.sub}</span>}
                  <p>{e.des}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== JOIN CTA ===================== */}
        <section className="section">
          <div className="wrap">
            <div className="lsa-cta reveal">
              <span className="eyebrow">Together we can, together we will</span>
              <h2>Join Jainam Jivika Foundation's Volunteer Family</h2>
              <p>
                Every step you take as a Life Saver Army volunteer protects a life. Whether you are registering donors, organising camps, or spreading awareness across your community - your contribution matters and the impact is real. Explore our opportunities and join the network that is saving lives across India.
              </p>
              <div className="lsa-cta-actions">
                <a href={VOLUNTEER_URL} target="_blank" rel="noopener noreferrer" className="btn btn--amber" data-burst="1">Join as a Volunteer →</a>
                <a href="https://lifesaverarmy.com" target="_blank" rel="noopener noreferrer" className="btn btn--outline-light">About Life Saver Army</a>
              </div>
            </div>
          </div>
        </section>
      </JjfShell>
    </Fragment>
  );
};

export default VolunteerApportunities;
